import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { bookingsAPI } from '../services/api';

interface CancellationReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

const CancellationReasonModal: React.FC<CancellationReasonModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [reasons, setReasons] = useState<string[]>([]);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadReasons();
    } else {
      setSelectedReason('');
    }
  }, [isOpen]);

  const loadReasons = async () => {
    try {
      setLoading(true);
      const response = await bookingsAPI.getCancellationReasons();
      if (response.success && response.data?.reasons) {
        setReasons(response.data.reasons);
      }
    } catch (error) {
      console.error('Error loading cancellation reasons:', error);
      // Fallback to default reasons
      setReasons([
        'Change of plans',
        'Found another service provider',
        'Service no longer needed',
        'Price too high',
        'Provider unavailable',
        'Emergency',
        'Other'
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (selectedReason) {
      onConfirm(selectedReason);
      setSelectedReason('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-t-2xl w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-xl font-bold text-gray-900">Why are you cancelling?</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-black600 animate-spin" />
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 px-6 pt-4 pb-2">
                Please select a reason for cancellation
              </p>
              <div className="px-6 pb-6">
                {reasons.map((reason, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedReason(reason)}
                    className={`w-full text-left p-4 mb-2 rounded-lg border-2 transition-all ${
                      selectedReason === reason
                        ? 'border-black bg-white'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center">
                      <div
                        className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                          selectedReason === reason
                            ? 'border-black bg-black'
                            : 'border-gray-300'
                        }`}
                      >
                        {selectedReason === reason && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                      <span
                        className={`${
                          selectedReason === reason
                            ? 'text-black600 font-semibold'
                            : 'text-gray-700'
                        }`}
                      >
                        {reason}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedReason}
            className={`flex-1 px-4 py-3 font-semibold rounded-lg transition-colors ${
              selectedReason
                ? 'bg-black text-white hover:bg-black700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Confirm Cancellation
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancellationReasonModal;

